/**
 * TenantSwitcher — dropdown en topbar para cambiar entre tenants del user.
 *
 * Replica el patrón Shopify (ver screenshots en ROADMAP §18):
 *   - Trigger: pill con avatar de iniciales (color por tenant) + nombre del activo
 *   - Dropdown:
 *     - Tenant activo (con check ✓)
 *     - Otros tenants (sin check)
 *     - Separador
 *     - "+ Crear tienda" → /stores/new
 *     - Separador
 *     - User info (avatar + nombre + email)
 *     - "Apariencia" (tema, color de marca y ancho; src/tema)
 *     - "Cerrar sesión" (con icono)
 *
 * Comportamiento:
 *   - Si el user tiene 1 solo tenant, se renderiza un avatar simple sin dropdown.
 *   - Si tiene >1, se muestra el dropdown completo.
 *   - Click en otro tenant → switchTenant() → SPA switch (V1).
 *
 * Roadmap §18 — Multi-tenant per user.
 */

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Popover, ActionList, Icon, Text, BlockStack, InlineStack, Box } from '@/lib/polaris';
import { CheckIcon, PlusIcon, ExitIcon, PersonIcon, DeleteIcon, ThemeIcon } from '@/lib/polaris-icons';
import { useAuth, useFeatureFlags, type TenantSummary } from '@smarty/shared';
import { DeleteStoreModal } from './DeleteStoreModal';
import { TenantAvatar } from './TenantAvatar';
import { abrirApariencia } from '@/tema/apariencia';

export function TenantSwitcher() {
  const { tenants, activeTenant, hasMultipleTenants, switchTenant, signOut, user, tenantId, storeSlug, userRole } = useAuth();
  // El plan del negocio activo: mismo predicado que la base y que `create-store` (`plan_type = 'drop'`).
  const { planType } = useFeatureFlags();
  // useNavigate directo (no useStoreNavigate): /stores/new es ruta global,
  // NO debe llevar prefijo /store/{slug}/ del tenant activo.
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const userName = useMemo(() => {
    const meta = user?.user_metadata as Record<string, unknown> | undefined;
    return ((meta?.full_name as string) || (meta?.name as string) || user?.email || 'Usuario') as string;
  }, [user]);

  // Resiliente: si la lista de tenants aún no cargó pero hay tenantId del
  // path legacy (resolveTenant), sintetizamos un activeTenant desde el state
  // legacy. Esto evita que el switcher desaparezca durante el bootstrap.
  const effectiveActiveTenant = useMemo<TenantSummary | null>(() => {
    if (activeTenant) return activeTenant;
    if (!tenantId) return null;
    return {
      id: tenantId,
      name: storeSlug ?? 'Mi tienda',
      slug: storeSlug,
      role: null,
      branchId: null,
    };
  }, [activeTenant, tenantId, storeSlug]);

  const handleSwitch = useCallback(async (id: string) => {
    setOpen(false);
    await switchTenant(id);
    // Telemetría de "opened" (Sprint 9). No-op si analytics no existe.
    try {
      const w = window as unknown as { analytics?: { track: (e: string, p: Record<string, unknown>) => void } };
      w.analytics?.track('tenant_switcher_opened', { total_tenants: tenants.length });
    } catch { /* ignore */ }
  }, [switchTenant, tenants.length]);

  const handleAddStore = useCallback(() => {
    setOpen(false);
    // Patrón Shopify: abrir wizard de creación en nueva pestaña.
    // El user mantiene su tienda actual abierta mientras configura la nueva.
    window.open('/stores/new', '_blank', 'noopener,noreferrer');
  }, []);

  const handleSignOut = useCallback(async () => {
    setOpen(false);
    await signOut();
  }, [signOut]);

  const handleDeleteStore = useCallback(() => {
    setOpen(false);
    setDeleteModalOpen(true);
  }, []);

  // Solo el owner puede ver/eliminar la tienda activa.
  // Usamos userRole del AuthContext (sincronizado con el tenant activo).
  // effectiveActiveTenant.role puede no estar populated si vino del fallback.
  const canDeleteActive = userRole === 'owner';

  // Si no hay tenant alguno (sin sesión / aún cargando), no renderizar
  if (!effectiveActiveTenant) return null;

  // Patrón Shopify: SIEMPRE mostrar dropdown, aunque haya 1 sola tienda.
  // Esto permite acceder a "+ Crear tienda" sin necesitar URL directa.
  // hasMultipleTenants se mantiene como info para futuras adaptaciones de UX.
  void hasMultipleTenants;
  const otherTenants = tenants.filter((t) => t.id !== effectiveActiveTenant.id);

  // Construimos las secciones del ActionList:
  // 1) Tenant activo con check
  // 2) Otros tenants
  // 3) "Crear tienda"
  // 4) User info + Cerrar sesión
  const items = [
    {
      content: effectiveActiveTenant.name,
      prefix: <TenantAvatar id={effectiveActiveTenant.id} name={effectiveActiveTenant.name} size={24} />,
      suffix: <Icon source={CheckIcon} tone="base" />,
      onAction: () => setOpen(false),
      active: true,
    },
    ...otherTenants.map((t) => ({
      content: t.name,
      prefix: <TenantAvatar id={t.id} name={t.name} size={24} />,
      onAction: () => void handleSwitch(t.id),
    })),
    /*
     * 🔒 EL REVENDEDOR NO VE «CREAR TIENDA». El founder lo encontró entrando a la cuenta del
     *    dropshipper: el menú se la ofrecía. El plan drop es una cuenta de reventa —vende el
     *    catálogo de Smarty Drop, no monta tiendas—, así que la opción no le corresponde.
     *
     * 🪤 Y ESTO SOLO ESCONDE EL ÍTEM. El corte de verdad está en `create-store`, que rechaza a
     *    quien ya tenga un negocio en plan drop: `/stores/new` es una URL que se escribe a mano.
     *    Esconder sin cerrar es el defecto del botón de suspender que no suspendía.
     */
    ...(planType === 'drop' ? [] : [{
      content: 'Crear tienda',
      prefix: <Icon source={PlusIcon} />,
      onAction: handleAddStore,
    }]),
    ...(canDeleteActive ? [{
      content: 'Eliminar tienda actual',
      prefix: <Icon source={DeleteIcon} tone="critical" />,
      onAction: handleDeleteStore,
      destructive: true,
    }] : []),
  ];

  const userItems = [
    {
      content: userName,
      helpText: user?.email ?? undefined,
      prefix: <Icon source={PersonIcon} />,
      onAction: () => setOpen(false),
    },
    {
      content: 'Apariencia',
      prefix: <Icon source={ThemeIcon} />,
      onAction: () => {
        setOpen(false);
        abrirApariencia();
      },
    },
    {
      content: 'Cerrar sesión',
      prefix: <Icon source={ExitIcon} />,
      onAction: () => void handleSignOut(),
      destructive: true,
    },
  ];

  /*
   * `data-selector-de-tienda` es el gancho que usa `polaris.css` para esconder esta pastilla
   * mientras el menú móvil está abierto: ahí el panel ya dice en qué tienda estás, y acá sería el
   * nombre repetido sobre un control que además no se puede usar. Va como atributo y no como clase
   * porque el estilo lo pinta esta misma función con `style`: no hay ninguna clase propia que una
   * hoja global pueda nombrar.
   */
  const trigger = (
    <button
      type="button"
      data-selector-de-tienda
      onClick={() => setOpen((v) => !v)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '4px 12px 4px 4px',
        borderRadius: 999,
        background: 'var(--p-color-bg-surface-secondary)',
        border: '1px solid var(--p-color-border)',
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: 600,
        color: 'var(--p-color-text)',
        height: 32,
      }}
      aria-label={`Tienda activa: ${effectiveActiveTenant.name}. Cambiar tienda.`}
      aria-expanded={open}
      aria-haspopup="menu"
    >
      <TenantAvatar id={effectiveActiveTenant.id} name={effectiveActiveTenant.name} size={24} />
      <span style={{ maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {effectiveActiveTenant.name}
      </span>
    </button>
  );

  return (
    <>
      <Popover
        active={open}
        activator={trigger}
        onClose={() => setOpen(false)}
        preferredAlignment="right"
        autofocusTarget="first-node"
      >
        <Box minWidth="280px">
          <ActionList items={items} />
          <Box borderBlockStartWidth="025" borderColor="border-subdued" paddingBlock="050">
            <BlockStack gap="0" />
          </Box>
          <ActionList items={userItems} />
        </Box>
      </Popover>
      <DeleteStoreModal
        open={deleteModalOpen}
        tenantId={effectiveActiveTenant.id}
        tenantName={effectiveActiveTenant.name}
        onClose={() => setDeleteModalOpen(false)}
      />
    </>
  );
}
